// El copy del aviso de venta trabada (paso 02) y las etiquetas de la ficha de Prospectos.
// Ninguna cadena de este archivo puede nombrar la causa del bloqueo: avisarla es delito.
import { daysSinceMx } from '@/lib/dateHelpers';
import type { BlockedCategory } from './types';

export const BLOCKED_TITLE = 'La cuenta todavía no está lista';
export const BLOCKED_SUB = 'La venta no puede continuar hasta que se resuelva.';

/** Rótulo del bloque del guion. El vendedor tiene al cliente enfrente y necesita decir algo. */
export const BLOCKED_SCRIPT_LABEL = 'Dile al cliente';

/**
 * Lo que el vendedor le lee al cliente, palabra por palabra. Sin guion cada quien improvisa, y ahí
 * es donde alguien menciona una lista.
 */
export const BLOCKED_CUSTOMER_SCRIPT =
    '«Tu cuenta está en revisión. En cuanto esté lista, puedes regresar por tu equipo. No se te cobró nada.»';

/** El caso terminal: la cuenta no se abre. Se dice que no continúa, pero nunca por qué. */
export const BLOCKED_REJECTED_TITLE = 'La cuenta no fue autorizada';
export const BLOCKED_REJECTED_SUB =
    'Esta venta no puede continuar. No se le cobró nada al cliente.';
export const BLOCKED_REJECTED_SCRIPT =
    '«No pudimos abrir tu cuenta, así que hoy no vamos a poder continuar con esta venta. No se te cobró nada. Si tienes dudas, puedes comunicarte con Fiado.»';

/** Qué se lee en el paso 02 según quién resuelve. El rechazo es el único que cierra la puerta. */
export interface BlockedCopy {
    title: string;
    sub: string;
    script: string;
}

export function blockedCopy(category: BlockedCategory | null | undefined): BlockedCopy {
    if (category === 'REJECTED') {
        return {
            title: BLOCKED_REJECTED_TITLE,
            sub: BLOCKED_REJECTED_SUB,
            script: BLOCKED_REJECTED_SCRIPT,
        };
    }
    return { title: BLOCKED_TITLE, sub: BLOCKED_SUB, script: BLOCKED_CUSTOMER_SCRIPT };
}

/** Espera normal del paso 02: mediana de 13 s medida en Dev, así que no se desglosa el avance. */
export const ACCOUNT_OPENING_TITLE = 'Estamos abriendo la cuenta';
export const ACCOUNT_OPENING_SUB = 'Tarda unos segundos. No cierres esta pantalla.';

/** Qué se lee en Prospectos por categoría: quién resuelve, sin decir por qué. */
const CATEGORY_TEXT: Record<BlockedCategory, string> = {
    CUSTOMER_RECORD: 'Falta corregir información del cliente. Repórtalo con tu gerente.',
    UNDER_REVIEW: 'El caso está en revisión. No hay nada que hacer de este lado.',
    TEMPORARY: 'Problema temporal del sistema. Se está reintentando solo.',
    REJECTED: 'La cuenta no fue autorizada. Esta venta no puede continuar.',
};

/** Sin categoría —la espera normal, o una que el silo agregó después— tampoco se deja en blanco. */
const CATEGORY_UNKNOWN_TEXT = 'La venta está detenida esperando la cuenta del cliente.';

/**
 * La categoría llega del silo en runtime: TypeScript cree el `Record` exhaustivo, pero una etiqueta
 * nueva pintaría un hueco donde el vendedor espera leer qué hacer.
 */
export function blockedCategoryText(category: BlockedCategory | null | undefined): string {
    if (!category) return CATEGORY_UNKNOWN_TEXT;
    return (
        (CATEGORY_TEXT as Partial<Record<BlockedCategory, string>>)[category] ??
        CATEGORY_UNKNOWN_TEXT
    );
}

/**
 * Desde cuándo lleva trabada, en días de calendario de México. Sale de `blocked.since`, no de
 * `updatedAt`: el reintento futuro va a mover `updatedAt` cada vuelta y todo diría «hoy».
 */
export function blockedSinceText(since: string): string {
    const days = daysSinceMx(since);
    if (days === 0) return 'hoy';
    if (days === 1) return 'hace 1 día';
    return `hace ${String(days)} días`;
}
